import React, { FC, useEffect, useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { notify } from '../../reducers/notificationReducer';
import { logoutUser } from '../../reducers/userReducer';
import SearchBar from '../SearchBar';
import {
  AppBar,
  Box,
  Button,
  Divider,
  SwipeableDrawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import HomeIcon from '@mui/icons-material/Home';
import MenuIcon from '@mui/icons-material/Menu';
import SearchIcon from '@mui/icons-material/Search';
import SettingsIcon from '@mui/icons-material/Settings';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import TuneIcon from '@mui/icons-material/Tune';
import GavelIcon from '@mui/icons-material/Gavel';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import GroupAddOutlinedIcon from '@mui/icons-material/GroupAddOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import PersonIcon from '@mui/icons-material/Person';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import Logo from '../Logo';
import MobileAuthModal from './MobileAuthModal';
import MobileHeaderSlot from './MobileHeaderSlot';
import HideOnScroll from '../HideOnScroll';
import { useMobileScrollHideEnabled } from './MobileHeaderContext';
import UserAvatar from '../UserAvatar';

interface MobileNavBarProps {
  /** The scroll container to watch, so the bar can hide on scroll-down. */
  scrollTarget?: HTMLElement | null;
  /**
   * Panel mode (SwipeTabViews pages): the bar overlays full-height tab panels
   * instead of occupying a layout slot, so content scrolls behind the glass.
   */
  overlay?: boolean;
}

const MobileNavBar: FC<MobileNavBarProps> = ({ scrollTarget, overlay = false }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  // Only feed/post pages opt into hide-on-scroll; elsewhere the header stays put.
  const scrollHide = useMobileScrollHideEnabled();
  // Callback-ref into state: the AppBar remounts when scrollHide toggles its
  // Slide wrapper, so the height observer must re-attach to the new node (a
  // plain ref + [] effect would keep watching the detached one and publish 0).
  const [barEl, setBarEl] = useState<HTMLElement | null>(null);

  // Publish the bar's height so tab panels can pad their scroll containers to
  // clear the overlaid glass header (content still scrolls behind it).
  useEffect(() => {
    if (!barEl || typeof ResizeObserver === 'undefined') return undefined;
    const update = () => {
      if (barEl.isConnected && barEl.offsetHeight > 0) {
        document.documentElement.style.setProperty('--mobile-header-h', `${barEl.offsetHeight}px`);
      }
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(barEl);
    return () => observer.disconnect();
  }, [barEl]);
  const user = useAppSelector(state => state.user);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  // Active query when on the results route — used to keep the expanded bar
  // prefilled so the search can be refined in place.
  const searchQuery = location.pathname.startsWith('/search/')
    ? decodeURIComponent(location.pathname.slice('/search/'.length))
    : '';

  // Keep the bar expanded while on search results; collapse it when leaving.
  useEffect(() => {
    setSearchOpen(location.pathname.startsWith('/search/'));
  }, [location.pathname]);

  // Profile lives in the drawer: go to own profile, or prompt login.
  const handleProfileClick = () => {
    setDrawerOpen(false);
    if (user?.username) {
      navigate(`/u/${user.username}`);
    } else {
      setAuthModalOpen(true);
    }
  };

  const goTo = (path: string) => {
    setDrawerOpen(false);
    navigate(path);
  };

  // Placeholder — incognito mode isn't built yet.
  const handleIncognitoClick = () => {
    setDrawerOpen(false);
    dispatch(notify('Incognito mode is coming soon!', 'info'));
  };

  const handleLogout = () => {
    if (!user) return;
    setDrawerOpen(false);
    dispatch(logoutUser());
    dispatch(notify(`u/${user.username} logged out`, 'success'));
  };

  const bar = (
    <AppBar
      ref={setBarEl}
      position={overlay ? 'absolute' : 'sticky'}
      color="inherit"
      elevation={0}
      sx={{
        ...(overlay ? { top: 0, left: 0, right: 0 } : {}),
        pt: 'env(safe-area-inset-top)',
        // Frosted glass — content scrolls behind and shows through blurred.
        backgroundColor: theme.tokens.colors.glassBackground,
        backdropFilter: theme.tokens.effects.glassBlur,
        WebkitBackdropFilter: theme.tokens.effects.glassBlur,
        borderBottom: 'var(--border-thin-solid)',
        borderColor: theme.tokens.colors.glassBorder,
        boxShadow: `inset 0 1px 0 ${theme.tokens.colors.glassSpec}`,
      }}
    >
      <Toolbar
        disableGutters
        sx={{
          px: 1,
          minHeight: { xs: '56px', sm: '64px' },
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        {!searchOpen ? (
          <>
            <IconButton
              aria-label="Open menu"
              size="large"
              color="inherit"
              onClick={() => setDrawerOpen(true)}
              sx={{ p: 1, '&:hover': { backgroundColor: theme.palette.action.hover } }}
            >
              <MenuIcon />
            </IconButton>
            <Button
              sx={{
                fontFamily: theme.tokens.typography.brandFontFamily,
                textTransform: 'none',
                fontSize: theme.tokens.typography.sizes.brand,
                padding: 0,
                minWidth: '110px',
                color: 'primary.main',
                '& .MuiButton-startIcon': {
                  width: '110px',
                  height: '45px',
                  '& .MuiBox-root': {
                    width: '100%',
                    height: '100%',
                    margin: 0,
                    display: 'flex',
                    '& svg': { width: 'auto', height: 'auto' },
                  },
                },
              }}
              component={RouterLink}
              to="/"
              startIcon={<Logo />}
              size="large"
            />

            <IconButton
              aria-label="Search"
              size="large"
              color="inherit"
              onClick={() => setSearchOpen(true)}
              sx={{ p: 1, '&:hover': { backgroundColor: theme.palette.action.hover } }}
            >
              <SearchIcon />
            </IconButton>
          </>
        ) : (
          // Expanded in-place search: replaces the menu + logo; submitting
          // navigates to /search/:query so results render below this bar.
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <SearchBar
              key={searchQuery}
              isMobile
              setSearchOpen={setSearchOpen}
              initialValue={searchQuery}
              autoFocus
            />
          </Box>
        )}
      </Toolbar>

      {/* Page-provided tabs or heading — part of the same glass bar, no separator. */}
      {!searchOpen && <MobileHeaderSlot />}
    </AppBar>
  );

  // Rendered OUTSIDE the AppBar: on feed pages the bar is wrapped in a Slide
  // (hide-on-scroll) whose transform would become the containing block for the
  // drawer's fixed-position edge swipe strip, pinning it to the header's height
  // instead of the full screen edge.
  const drawer = (
      <SwipeableDrawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onOpen={() => setDrawerOpen(true)}
        // MUI disables edge swipe-to-open on iOS Safari (back-gesture clash);
        // inside the Capacitor webview there is no browser gesture, so enable it.
        disableSwipeToOpen={false}
        swipeAreaWidth={24}
        disableDiscovery
        PaperProps={{ sx: { width: 260, pt: 'env(safe-area-inset-top)' } }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'fontWeightBold', color: 'primary.main' }}>
            Menu
          </Typography>
        </Box>
        <Divider />
        <List>
          <ListItemButton onClick={handleProfileClick}>
            <ListItemIcon sx={{ minWidth: 44, color: 'primary.main' }}>
              {user ? (
                // frame="none": nav chrome shows who you are, not what you own.
                <UserAvatar
                  user={user as any}
                  size={32}
                  frame="none"
                  sx={{ bgcolor: theme.palette.secondary.main }}
                />
              ) : (
                <PersonIcon />
              )}
            </ListItemIcon>
            <ListItemText
              primary={user ? `u/${user.username}` : 'Log in'}
              secondary={user ? 'View profile' : 'Access your account'}
              primaryTypographyProps={{ fontWeight: 'fontWeightBold' }}
            />
          </ListItemButton>
          <ListItemButton onClick={handleIncognitoClick}>
            <ListItemIcon sx={{ minWidth: 44, color: 'primary.main' }}>
              <VisibilityOffIcon />
            </ListItemIcon>
            <ListItemText
              primary="Switch to incognito mode"
              primaryTypographyProps={{ fontWeight: 'fontWeightBold' }}
            />
          </ListItemButton>
          <Divider sx={{ my: 0.5 }} />
          <ListItemButton onClick={() => goTo('/')}>
            <ListItemIcon sx={{ minWidth: 44, color: 'primary.main' }}>
              <HomeIcon />
            </ListItemIcon>
            <ListItemText primary="Home" primaryTypographyProps={{ fontWeight: 'fontWeightBold' }} />
          </ListItemButton>
          <ListItemButton onClick={() => goTo('/preferences')}>
            <ListItemIcon sx={{ minWidth: 44, color: 'primary.main' }}>
              <TuneIcon />
            </ListItemIcon>
            <ListItemText primary="Preferences" primaryTypographyProps={{ fontWeight: 'fontWeightBold' }} />
          </ListItemButton>
          <ListItemButton onClick={() => goTo('/settings')}>
            <ListItemIcon sx={{ minWidth: 44, color: 'primary.main' }}>
              <SettingsIcon />
            </ListItemIcon>
            <ListItemText primary="Settings" primaryTypographyProps={{ fontWeight: 'fontWeightBold' }} />
          </ListItemButton>
          <ListItemButton onClick={() => goTo('/wallet')}>
            <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
              <AccountBalanceWalletIcon />
            </ListItemIcon>
            <ListItemText primary="Wallet" primaryTypographyProps={{ fontWeight: 'fontWeightBold' }} />
          </ListItemButton>
          <ListItemButton onClick={() => goTo('/earn')}>
            <ListItemIcon sx={{ minWidth: 44, color: 'primary.main' }}>
              <PaidOutlinedIcon />
            </ListItemIcon>
            <ListItemText primary="Earn" primaryTypographyProps={{ fontWeight: 'fontWeightBold' }} />
          </ListItemButton>
          <ListItemButton onClick={() => goTo('/referrals')}>
            <ListItemIcon sx={{ minWidth: 44, color: 'primary.main' }}>
              <GroupAddOutlinedIcon />
            </ListItemIcon>
            <ListItemText primary="Referral Program" primaryTypographyProps={{ fontWeight: 'fontWeightBold' }} />
          </ListItemButton>
          <ListItemButton onClick={() => goTo('/creator-program')}>
            <ListItemIcon sx={{ minWidth: 44, color: 'primary.main' }}>
              <AutoAwesomeOutlinedIcon />
            </ListItemIcon>
            <ListItemText primary="Creator Program" primaryTypographyProps={{ fontWeight: 'fontWeightBold' }} />
          </ListItemButton>
          <ListItemButton onClick={() => goTo('/promote')}>
            <ListItemIcon sx={{ minWidth: 44, color: 'primary.main' }}>
              <CampaignOutlinedIcon />
            </ListItemIcon>
            <ListItemText primary="Promote" primaryTypographyProps={{ fontWeight: 'fontWeightBold' }} />
          </ListItemButton>
          <ListItemButton onClick={() => goTo('/help')}>
            <ListItemIcon sx={{ minWidth: 44, color: 'primary.main' }}>
              <HelpOutlineIcon />
            </ListItemIcon>
            <ListItemText primary="Help" primaryTypographyProps={{ fontWeight: 'fontWeightBold' }} />
          </ListItemButton>
          <ListItemButton onClick={() => goTo('/report-abuse')}>
            <ListItemIcon sx={{ minWidth: 44, color: 'primary.main' }}>
              <FlagOutlinedIcon />
            </ListItemIcon>
            <ListItemText primary="Report Abuse" primaryTypographyProps={{ fontWeight: 'fontWeightBold' }} />
          </ListItemButton>
          <ListItemButton onClick={() => goTo('/policies')}>
            <ListItemIcon sx={{ minWidth: 44, color: 'primary.main' }}>
              <GavelIcon />
            </ListItemIcon>
            <ListItemText primary="Policies" primaryTypographyProps={{ fontWeight: 'fontWeightBold' }} />
          </ListItemButton>
        </List>

        {/* Logout pinned to the drawer's bottom (drawer paper is a flex column). */}
        {user && (
          <Box sx={{ mt: 'auto', pb: 'env(safe-area-inset-bottom)' }}>
            <Divider />
            <List disablePadding>
              <ListItemButton onClick={handleLogout}>
                <ListItemIcon sx={{ minWidth: 44, color: 'primary.main' }}>
                  <PowerSettingsNewIcon />
                </ListItemIcon>
                <ListItemText primary="Logout" primaryTypographyProps={{ fontWeight: 'fontWeightBold' }} />
              </ListItemButton>
            </List>
          </Box>
        )}
      </SwipeableDrawer>
  );

  return (
    <>
      {scrollHide ? <HideOnScroll target={scrollTarget}>{bar}</HideOnScroll> : bar}
      {drawer}
      <MobileAuthModal open={authModalOpen} onClose={() => setAuthModalOpen(false)} />
    </>
  );
};

export default MobileNavBar;
